import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Save, X } from "lucide-react";
import DateInput from "./DateInput";

export default function AdminOrderEditPopup({ order, onClose, onUpdated }) {
  const isPreorder = order.order_type === "preorder";
  const [form, setForm] = useState({
    customer_name: order.customer_name || "",
    customer_email: order.customer_email || "",
    customer_phone: order.customer_phone || "",
    shipping_address: order.shipping_address || "",
    city: order.city || "",
    state: order.state || "",
    pincode: order.pincode || "",
    booking_amount: order.booking_amount ?? "",
    price_offered: order.price_offered ?? order.total ?? "",
    delivery_required: order.delivery_required || false,
    delivery_included: order.delivery_included || false,
    release_date: order.release_date || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.customer_name || !form.customer_phone) {
      toast.error("Name and phone are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        shipping_address: form.shipping_address,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
      };
      if (isPreorder) {
        payload.booking_amount = form.booking_amount !== "" ? Number(form.booking_amount) : null;
        payload.price_offered = form.price_offered !== "" ? Number(form.price_offered) : null;
        payload.delivery_required = form.delivery_required;
        payload.delivery_included = form.delivery_included;
        payload.release_date = form.release_date || null;
      }
      await api.patch(`/admin/orders/${order.id}`, payload);
      toast.success("Order details updated");
      onClose();
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy-900/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-navy-800 rounded-2xl p-6 max-w-lg w-full neon-border max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-gold">Edit Details</div>
            <div className="text-xl font-black">#{order.order_number}</div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Customer Name *</span>
            <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Phone *</span>
            <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Email</span>
            <input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Shipping Address</span>
            <textarea value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} rows={2} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60 resize-none" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">City</span>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">State</span>
              <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Pincode</span>
              <input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
            </label>
          </div>

          {isPreorder && (
            <>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Release Date</span>
                <DateInput
                  value={form.release_date}
                  onChange={(e) => setForm({ ...form, release_date: e.target.value })}
                  className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Booking Amount Collected</span>
                  <input type="number" min={0} value={form.booking_amount} onChange={(e) => setForm({ ...form, booking_amount: e.target.value })} placeholder="e.g. 500" className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Total Price Offered</span>
                  <input type="number" min={0} value={form.price_offered} onChange={(e) => setForm({ ...form, price_offered: e.target.value })} placeholder="e.g. 4999" className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
                </label>
              </div>

              <div className="border border-gold/15 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-200">Delivery Required?</span>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, delivery_required: !form.delivery_required, delivery_included: false })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.delivery_required ? "bg-gold" : "bg-navy-600"}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.delivery_required ? "translate-x-6" : "translate-x-1"}`} />
                  </button>
                </div>
                {form.delivery_required && (
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, delivery_included: false })}
                      className={`flex-1 text-xs py-2 rounded-full border font-bold uppercase tracking-wider transition ${!form.delivery_included ? "bg-gold text-navy-900 border-gold" : "border-gold/30 text-slate-400 hover:border-gold/50"}`}
                    >
                      Extra Charge
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, delivery_included: true })}
                      className={`flex-1 text-xs py-2 rounded-full border font-bold uppercase tracking-wider transition ${form.delivery_included ? "bg-gold text-navy-900 border-gold" : "border-gold/30 text-slate-400 hover:border-gold/50"}`}
                    >
                      Included in Price
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 border border-gold/30 text-slate-300 font-bold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-navy-700">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-gold-light disabled:opacity-50 flex items-center justify-center gap-2">
            <Save className="w-3.5 h-3.5" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
